'use client';

/** @jsxImportSource @emotion/react */
import type { ReactNode } from 'react';

import {
  Button,
  compactObject,
  FlexItem,
  FlexLayout,
  LazyBackground,
  Typography,
} from '@wci-tech/core-ui';

import { appConfig } from '@/config/appConfig';
import { useThemeMode } from '@/context';

import { BookADemoButton } from '../book-a-demo-button';
import { useStyles } from './BringOrder.styles';
import { useScrollToTarget } from '../../hooks/useScrollToTarget';

type BringOrderProps = {
  title?: string;
  description?: string;
  primaryBtn?: string;
  secondaryBtn?: string;
  secondaryBtnLink?: string;
  secondaryButton?: ReactNode;
  secondaryBtnTargetId?: string;
  secondaryBtnScrollOffset?: number;
  id?: string;
  showPrimaryButton?: boolean;
};

const BringOrder = ({
  title,
  description,
  primaryBtn,
  secondaryBtn,
  secondaryBtnLink,
  secondaryButton,
  secondaryBtnTargetId,
  secondaryBtnScrollOffset = 82,
  id,
  showPrimaryButton = true,
}: BringOrderProps) => {
  const styles = useStyles();
  const { isDark } = useThemeMode();

  const desktopImg = isDark
    ? './images/BringOrderDesktopDark.png'
    : './images/BringOrderDesktopLight.png';

  const tabletImg = isDark
    ? './images/BringOrderTabletDark.png'
    : './images/BringOrderTabletLight.png';

  const mobileImg = isDark
    ? './images/BringOrderMobileDark.png'
    : './images/BringOrderMobileLight.png';

  const handleSecondaryButtonClick = useScrollToTarget({
    targetId: secondaryBtnTargetId,
    offset: secondaryBtnScrollOffset,
  });

  const secondaryButtonProps = compactObject({
    href: secondaryBtnTargetId ? undefined : secondaryBtnLink,
    onClick: secondaryBtnTargetId ? handleSecondaryButtonClick : undefined,
  });

  return (
    <section css={styles.wrapper} id={id}>
      <LazyBackground
        desktopImgSrc={desktopImg}
        tabletImgSrc={tabletImg}
        mobileImgSrc={mobileImg}
        interactionEffect="cursor-follow"
        interactionIntensity={100}
        customStyles={{
          width: '100%',
        }}
      >
        <FlexLayout
          direction="column"
          alignItems="center"
          justifyContent="center"
          gap={{ default: 0 }}
          css={styles.contentWrapper}
        >
          <FlexItem css={styles.headerSection}>
            <FlexLayout
              direction="column"
              alignItems="center"
              gap={{ default: 8, md: 12, lg: 16 }}
              padding={{ default: 0 }}
            >
              <Typography variant="h2" customStyles={styles.title}>
                {title}
              </Typography>

              <Typography variant="p" customStyles={styles.description}>
                {description}
              </Typography>
            </FlexLayout>
          </FlexItem>

          <FlexItem css={styles.buttonfullWidthContainer}>
            <FlexLayout
              direction={{ default: 'column', md: 'row' }}
              justifyContent="center"
              gap={{ default: 20, md: 12, lg: 18 }}
              padding={{ default: 0 }}
            >
              {showPrimaryButton && (
                <FlexItem>
                  <BookADemoButton
                    effect="three-d"
                    shape="pill"
                    buttonText={primaryBtn}
                    customStyles={{
                      base: styles.demoButton,
                    }}
                    calendlyUrl={appConfig.integrations.calendly.eventUrl}
                  />
                </FlexItem>
              )}

              <FlexItem>
                {secondaryButton ?? (
                  <Button
                    effect="three-d"
                    variant="secondary"
                    shape="pill"
                    css={styles.buttonfullWidthContainer}
                    {...secondaryButtonProps}
                  >
                    {secondaryBtn}
                  </Button>
                )}
              </FlexItem>
            </FlexLayout>
          </FlexItem>
        </FlexLayout>
      </LazyBackground>
    </section>
  );
};

export default BringOrder;
